import useFiltersAssociationByProfileWithDefaults from 'app/hooks/useFiltersAssociationByProfileWithDefaults';
import useProcessingFiltersListWithDefaults from 'app/hooks/useProcessingFiltersListWithDefaults';
import { StoredFilterDescription } from 'app/redux/processing/reducer';
import { CONFIGURABLE_XRAY_REGIONS, getAnatomicRegionFromString } from 'app/utils/xrayRegions';
import _ from 'lodash';
import { useCallback } from 'react';

export type ProcessingOptionsProvider = {
  getProcessingOptions: (
    processingType: string,
    anatomicRegionWithSpecie: string
  ) => StoredFilterDescription;
};

export default function useProcessingOptionsProvider() {
  const processingFilters = useProcessingFiltersListWithDefaults();
  const filtersAssociationByProfile = useFiltersAssociationByProfileWithDefaults();

  function getFilterById(filterId: string) {
    return processingFilters.find((f) => f.id === filterId);
  }

  const getProcessingOptions = useCallback(
    (processingType: string, anatomicRegionWithSpecie: string) => {
      if (getFilterById(processingType)) return _.cloneDeep(getFilterById(processingType));

      const { specie, mainRegion, subRegion } =
        getAnatomicRegionFromString(anatomicRegionWithSpecie);

      let regionsParts: string[] = [];
      if (Object.keys(CONFIGURABLE_XRAY_REGIONS.bones).includes(mainRegion)) {
        regionsParts.push('bones', mainRegion, subRegion);
      } else {
        regionsParts.push(mainRegion, subRegion);
      }
      regionsParts = _.filter(regionsParts);
      const keysToTry = _.times(regionsParts.length, (i) =>
        regionsParts.slice(0, regionsParts.length - i).join(' ')
      );

      for (const key of keysToTry) {
        const possibleFilter = getFilterById(filtersAssociationByProfile[specie]?.[key]);

        if (possibleFilter) {
          return _.cloneDeep(possibleFilter);
        }
      }
      if (getFilterById(filtersAssociationByProfile[specie]?.default)) {
        return _.cloneDeep(getFilterById(filtersAssociationByProfile[specie]?.default));
      }
      for (const key of keysToTry) {
        const possibleFilter = getFilterById(filtersAssociationByProfile.all?.[key]);
        if (possibleFilter) {
          return _.cloneDeep(possibleFilter);
        }
      }
      return _.cloneDeep(getFilterById(filtersAssociationByProfile.all?.default));
    },
    [filtersAssociationByProfile, processingFilters]
  );

  return { getProcessingOptions };
}
